/**
 * The details column for one room.
 *
 * Five accordion sections, of which a reader only ever sees the ones they can
 * actually act on: a section that would answer 403 the moment it is opened is
 * worse than no section at all, so permission decides existence rather than a
 * disabled control. Only the expanded section is mounted, which keeps the
 * sharing request from being made by everyone who merely opens the column.
 */

import { useCallback, useId, useMemo, useRef, useState } from 'react'
import type { KeyboardEvent as ReactKeyboardEvent, ReactElement } from 'react'

import { useAuth } from '@/api/auth'
import type { CurrentUser, MemberRole, PermissionRole, Room, ServerPermissions } from '@/api/types'
import { IconButton } from '@/components/ui'
import { MemberList, roleRank } from '@/components/MemberList'
import { RoomFiles } from '@/components/RoomFiles'
import { RoomSettings } from '@/components/RoomSettings'
import { RoomSharing } from '@/components/RoomSharing'
import { RoomWebhooks } from '@/components/RoomWebhooks'
import styles from './DetailsPanel.module.css'

export type DetailsTab = 'members' | 'files' | 'sharing' | 'webhooks' | 'settings'

const TAB_ORDER = ['members', 'files', 'sharing', 'webhooks', 'settings'] as const

const TAB_LABELS: Record<DetailsTab, string> = {
  members: 'Members',
  files: 'Files',
  sharing: 'Sharing',
  webhooks: 'Webhooks',
  settings: 'Settings',
}

const TAB_STORAGE_KEY = 'relaii.details-tab'

function isDetailsTab(value: string | null): value is DetailsTab {
  return value !== null && (TAB_ORDER as readonly string[]).includes(value)
}

function readPreferredTab(): DetailsTab | null {
  try {
    const stored = window.localStorage.getItem(TAB_STORAGE_KEY)
    return isDetailsTab(stored) ? stored : null
  } catch {
    return null
  }
}

function writePreferredTab(tab: DetailsTab): void {
  try {
    window.localStorage.setItem(TAB_STORAGE_KEY, tab)
  } catch {
    // Private browsing refuses to store anything. Losing the preference on
    // reload is not worth failing the render over.
  }
}

// ── Permissions ─────────────────────────────────────────────────────────────

export interface RoomAbilities {
  /** Null for a public channel the caller can read but has not joined. */
  role: MemberRole | null
  isAdmin: boolean
  /** Owner or moderator here, or an administrator of this server. */
  canModerate: boolean
  canManageMembers: boolean
  /** Only an owner or an administrator may hand out ownership. */
  canAssignOwner: boolean
  /** Whether "Add people" will be accepted: the room's kind, then server policy. */
  canInvite: boolean
  canDeleteFiles: boolean
  /** The room's owner or a server administrator, in a room this server owns. */
  canManageSharing: boolean
  canEditRoom: boolean
  canDeleteRoom: boolean
}

/**
 * What this user may do in this room.
 *
 * Nothing here is a security decision — the server makes those — but getting
 * it right is what keeps the panel from offering an action that will be
 * refused. Two rules run through all of it: a room another server owns is that
 * server's to configure, and a guest is a reader.
 */
export function roomAbilities(
  room: Room,
  user: CurrentUser | null,
  permissions?: Partial<ServerPermissions>,
): RoomAbilities {
  const role = room.my_membership?.role ?? null
  const isAdmin = user?.is_admin === true
  const isOwner = role === 'owner'
  const canModerate = isOwner || role === 'moderator' || isAdmin
  const local = room.is_local
  const channel = room.kind === 'channel'

  return {
    role,
    isAdmin,
    canModerate,
    canManageMembers: canModerate && local,
    canAssignOwner: isOwner || isAdmin,
    canInvite: mayInvite(room, role, isAdmin, permissions),
    canDeleteFiles: canModerate,
    // Sharing is the owner's call, not a moderator's (`:share`).
    canManageSharing: (isOwner || isAdmin) && local,
    canEditRoom: canModerate && local && channel,
    canDeleteRoom: (isOwner || isAdmin) && local,
  }
}

/** The policy a server without `permissions` in `GET /server` ships with. */
const DEFAULT_INVITE_ROLE: PermissionRole = 'admin'

/** Whether a room role reaches `minimum` on the room ladder. */
function roleAtLeast(role: MemberRole | null, minimum: MemberRole): boolean {
  return role !== null && roleRank(role) >= roleRank(minimum)
}

/**
 * The server's `:invite` rule (`Rooms.Permissions`), seen from the client.
 *
 * A direct conversation never takes anyone, a group takes whoever its members
 * bring, and a channel asks for a moderator of it — or a server admin — who
 * the `invite_users` policy also lets through. That policy's `moderator` and
 * `owner` are judged by the role in this room (`Orgs.permits?/3`), `member`
 * lets every local account through, and `admin` only admins.
 */
function mayInvite(
  room: Room,
  role: MemberRole | null,
  isAdmin: boolean,
  permissions: Partial<ServerPermissions> | undefined,
): boolean {
  if (!room.is_local || room.archived_at !== null) return false
  if (room.kind === 'direct') return false
  if (room.kind === 'group_direct') return role !== null
  if (isAdmin) return true
  if (!roleAtLeast(role, 'moderator')) return false

  const minimum = permissions?.invite_users ?? DEFAULT_INVITE_ROLE
  if (minimum === 'moderator' || minimum === 'owner') return roleAtLeast(role, minimum)
  return minimum === 'member'
}

function visibleTabs(
  room: Room,
  abilities: RoomAbilities,
  federationEnabled: boolean,
  webhooksEnabled: boolean,
): DetailsTab[] {
  const tabs: DetailsTab[] = ['members', 'files']

  // A channel owned elsewhere always explains who owns it and what this server
  // was granted; a channel owned here only concerns whoever can change that.
  if (room.kind === 'channel') {
    const showSharing = room.is_local ? federationEnabled && abilities.canManageSharing : true
    if (showSharing) tabs.push('sharing')
    if (room.is_local && abilities.isAdmin && webhooksEnabled) tabs.push('webhooks')
  }

  if (abilities.canEditRoom) tabs.push('settings')

  return tabs
}

// ── Icons ───────────────────────────────────────────────────────────────────

function CloseIcon(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true" focusable="false">
      <path
        d="m4.5 4.5 7 7m0-7-7 7"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  )
}

function ChevronIcon(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true" focusable="false">
      <path d="m4 6 4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function SectionIcon({ tab }: { tab: DetailsTab }): ReactElement {
  if (tab === 'members') {
    return (
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">
        <circle cx="7.25" cy="7" r="2.75" stroke="currentColor" strokeWidth="1.5" />
        <path d="M2.75 16c.35-3 2-4.5 4.5-4.5s4.15 1.5 4.5 4.5M12.25 5.25a2.5 2.5 0 0 1 0 4.85M13.1 11.55c2.35.25 3.7 1.7 4 4.45" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    )
  }

  if (tab === 'files') {
    return (
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">
        <path d="M5 2.75h6l4 4v10.5H5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M11 2.75v4h4M7.75 10h4.5M7.75 13h4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    )
  }

  if (tab === 'sharing') {
    return (
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">
        <circle cx="5" cy="10" r="2.25" stroke="currentColor" strokeWidth="1.5" />
        <circle cx="15" cy="5" r="2.25" stroke="currentColor" strokeWidth="1.5" />
        <circle cx="15" cy="15" r="2.25" stroke="currentColor" strokeWidth="1.5" />
        <path d="m7 9 5.8-2.9M7 11l5.8 2.9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    )
  }

  if (tab === 'webhooks') {
    return (
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">
        <path
          d="M7.25 6.25 4.5 9a2.1 2.1 0 0 0 3 3l2.25-2.25m3-3L15.5 4a2.1 2.1 0 0 1 3 3l-2.75 2.75m-8.5 3.5L4.5 16a2.1 2.1 0 0 0 3 3l2.75-2.75m3.5-3.5L16.5 10a2.1 2.1 0 0 1 3 3l-2.75 2.75"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    )
  }

  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">
      <path d="M4 5.5h12M4 10h12M4 14.5h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="8" cy="5.5" r="1.75" fill="var(--surface-raised)" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="13" cy="10" r="1.75" fill="var(--surface-raised)" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="7" cy="14.5" r="1.75" fill="var(--surface-raised)" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  )
}

// ── Panel ───────────────────────────────────────────────────────────────────

export interface DetailsPanelProps {
  room: Room
  /** True on a phone, where the column covers the conversation instead of sitting beside it. */
  compact?: boolean
  /** Collapses the column. The shell owns whether it is open, and remembers it. */
  onClose?: () => void
}

export function DetailsPanel({ room, compact = false, onClose }: DetailsPanelProps): ReactElement {
  const { user, server } = useAuth()
  const baseId = useId()
  const buttonsRef = useRef(new Map<DetailsTab, HTMLButtonElement | null>())

  const [preferred, setPreferred] = useState<DetailsTab | null>(readPreferredTab)
  const [collapsed, setCollapsed] = useState(false)

  const abilities = useMemo(() => roomAbilities(room, user, server?.permissions), [room, user, server])
  const tabs = useMemo(
    () =>
      visibleTabs(
        room,
        abilities,
        server?.federation_enabled ?? false,
        server?.capabilities?.webhooks !== false,
      ),
    [room, abilities, server],
  )

  // The remembered tab may not exist in this room — channel settings followed
  // by a direct message, say — so the first available one stands in without
  // overwriting what the reader chose.
  const active: DetailsTab | null = collapsed
    ? null
    : preferred !== null && tabs.includes(preferred)
      ? preferred
      : (tabs[0] ?? null)

  const select = useCallback((tab: DetailsTab): void => {
    setCollapsed(false)
    setPreferred(tab)
    writePreferredTab(tab)
  }, [])

  const onSectionKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLButtonElement>): void => {
      const current = tabs.findIndex((tab) => buttonsRef.current.get(tab) === event.currentTarget)
      if (current === -1) return

      let next = -1
      if (event.key === 'ArrowDown') next = (current + 1) % tabs.length
      else if (event.key === 'ArrowUp') next = (current - 1 + tabs.length) % tabs.length
      else if (event.key === 'Home') next = 0
      else if (event.key === 'End') next = tabs.length - 1
      else return

      const target = tabs[next]
      if (target === undefined) return

      event.preventDefault()
      select(target)
      buttonsRef.current.get(target)?.focus()
    },
    [select, tabs],
  )

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <div className={styles.heading}>
          <h2 className={styles.title}>Room details</h2>
        </div>
        {onClose !== undefined ? (
          <IconButton
            label={compact ? 'Close details' : 'Hide details'}
            size="sm"
            variant="ghost"
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        ) : null}
      </div>

      {room.topic !== null && room.topic !== '' ? (
        <p className={styles.topic} title={room.topic}>{room.topic}</p>
      ) : null}

      <div className={styles.accordion}>
        {tabs.map((tab) => {
          const expanded = tab === active

          return (
            <section className={styles.accordionSection} key={tab}>
              <h3 className={styles.accordionHeading}>
                <button
                  ref={(node) => {
                    buttonsRef.current.set(tab, node)
                  }}
                  type="button"
                  id={`${baseId}-section-${tab}`}
                  className={styles.accordionButton}
                  aria-expanded={expanded}
                  aria-controls={expanded ? `${baseId}-panel-${tab}` : undefined}
                  onClick={() => {
                    if (tab === active) setCollapsed(true)
                    else select(tab)
                  }}
                  onKeyDown={onSectionKeyDown}
                >
                  <span className={styles.accordionLabel}>
                    <span className={styles.sectionIcon} aria-hidden="true">
                      <SectionIcon tab={tab} />
                    </span>
                    <span>{TAB_LABELS[tab]}</span>
                    {tab === 'members' ? (
                      <span className={styles.sectionCount}>{room.member_count}</span>
                    ) : null}
                  </span>
                  {tab === 'members' ? null : <span className={styles.chevron} aria-hidden="true"><ChevronIcon /></span>}
                </button>
              </h3>

              {/* The column stays mounted from one room to the next, so each
                  section is keyed by the room: a half-typed webhook name or a
                  file filter belongs to the room it was typed in. */}
              {expanded ? (
                <div
                  className={styles.accordionPanel}
                  role="region"
                  id={`${baseId}-panel-${tab}`}
                  aria-labelledby={`${baseId}-section-${tab}`}
                >
                  {tab === 'members' ? (
                    <MemberList
                      key={room.id}
                      room={room}
                      canManage={abilities.canManageMembers}
                      canInvite={abilities.canInvite}
                      allowAdd={false}
                    />
                  ) : null}

                  {tab === 'files' ? (
                    <RoomFiles key={room.id} room={room} canDelete={abilities.canDeleteFiles} />
                  ) : null}

                  {tab === 'sharing' ? (
                    <RoomSharing
                      key={room.id}
                      room={room}
                      canManage={abilities.canManageSharing}
                      canChoosePeers={abilities.isAdmin}
                    />
                  ) : null}

                  {tab === 'webhooks' ? <RoomWebhooks key={room.id} room={room} compact /> : null}

                  {tab === 'settings' ? (
                    <RoomSettings key={room.id} room={room} canDelete={abilities.canDeleteRoom} />
                  ) : null}
                </div>
              ) : null}
            </section>
          )
        })}
      </div>
    </div>
  )
}

export default DetailsPanel
